import { useEffect, useMemo, useState } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import './App.css';
import { useAuth } from './auth/AuthContext';
import AuthModal from './components/AuthModal';
import Sidebar from './components/Sidebar';
import PageView from './components/PageView';
import GameRouteGate from './components/GameRouteGate';
import GamePage from './games/GamePage';
import NotFoundPage from './components/NotFoundPage';
import { ShellSidebarContext } from './shell/ShellSidebarContext';

function MainShell() {
  const { authModalOpen, authChecked } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isNarrow, setIsNarrow] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 860px)').matches
  );

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 860px)');
    const sync = () => setIsNarrow(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 861px)');
    const onChange = () => {
      if (mq.matches) {
        setSidebarOpen(false);
      }
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    if (!sidebarOpen || authModalOpen) {
      return undefined;
    }
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setSidebarOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sidebarOpen, authModalOpen]);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 860px)');
    const syncBody = () => {
      document.body.style.overflow = mq.matches && sidebarOpen && !authModalOpen ? 'hidden' : '';
    };
    syncBody();
    mq.addEventListener('change', syncBody);
    return () => {
      mq.removeEventListener('change', syncBody);
      document.body.style.overflow = '';
    };
  }, [sidebarOpen, authModalOpen]);

  /** Keep listener on the shell so browser Back still runs before the game route unmounts. */
  useEffect(() => {
    const inMiniGame = authChecked && /^\/games\/[^/]+/.test(location.pathname);
    if (!inMiniGame) {
      return undefined;
    }
    const onPopState = () => {
      window.history.go(1);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [authChecked, location.pathname]);

  const shellSidebarValue = useMemo(
    () => ({
      sidebarOpen,
      setSidebarOpen,
      toggleSidebar: () => setSidebarOpen((open) => !open),
    }),
    [sidebarOpen],
  );

  return (
    <ShellSidebarContext.Provider value={shellSidebarValue}>
      <div className={`app-shell${sidebarOpen ? ' app-shell--drawer-open' : ''}`}>
        <button
          type="button"
          className="sidebar-backdrop"
          aria-label="Close menu"
          aria-hidden={!sidebarOpen}
          tabIndex={sidebarOpen ? 0 : -1}
          onClick={() => setSidebarOpen(false)}
        />
        <Sidebar
          drawerOpen={sidebarOpen}
          onCloseDrawer={() => setSidebarOpen(false)}
          ariaHidden={isNarrow && !sidebarOpen}
        />
        <div className="app-main">
          <Outlet />
        </div>
        <AuthModal />
      </div>
    </ShellSidebarContext.Provider>
  );
}

function App() {
  return (
    <Routes>
      <Route element={<MainShell />}>
        <Route path="/" element={<Navigate to="/games/paper-fly" replace />} />
        <Route path="/hub" element={<PageView tab="hub" />} />
        <Route path="/profile" element={<PageView tab="profile" />} />
        <Route path="/settings" element={<PageView tab="settings" />} />
        <Route
          path="/games/:gameSlug"
          element={
            <GameRouteGate>
              <GamePage />
            </GameRouteGate>
          }
        />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}

export default App;
